import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { PaginatedResult } from '../../../common/entities/paginated-result.entity';
import { API_BASE_URL } from '../../../common/tokens/api-base-url.token';
import {
  OPEN_CORRECTIVE_ACTION_STATUSES,
} from '../../application/entities/corrective-action-status.entity';
import { CorrectiveActionSummary } from '../../application/entities/corrective-action-summary.entity';
import { DashboardFilters } from '../../application/entities/dashboard-filters.entity';
import { RecentCorrectiveActionsProviderPort } from '../../application/ports/recent-corrective-actions-provider.port';
import { RecentCorrectiveActionsResponseDto } from '../dto/recent-corrective-actions-response.dto';
import { toCorrectiveActionSummary } from '../mappers/recent-corrective-actions.mapper';

/** Filas por página de la tabla de acciones correctivas prioritarias. */
export const CORRECTIVE_ACTIONS_PAGE_SIZE = 5;

@Injectable({ providedIn: 'root' })
export class RecentCorrectiveActionsProviderService implements RecentCorrectiveActionsProviderPort {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  /**
   * POST /requirements/get: las medidas abiertas por prioridad, de 5 en 5. Orden: mayor riesgo primero,
   * a igual riesgo la menos avanzada, y por último la más reciente (desempate estable).
   * El aislamiento por organización lo aplica el backend; cliente, planta y nivel de riesgo del
   * dashboard se envían como filtros.
   */
  getRecent(filters?: DashboardFilters): Observable<PaginatedResult<CorrectiveActionSummary>> {
    const page = Math.max(filters?.actionsPage ?? 1, 1);
    const params = {
      init: (page - 1) * CORRECTIVE_ACTIONS_PAGE_SIZE,
      limit: CORRECTIVE_ACTIONS_PAGE_SIZE,
      total: true,
      orderBy: ['-riskLevel', '+statusProgress', '-updatedAt'],
    };
    const values: Record<string, unknown>[] = [
      { property: 'status', propType: 'STRING', propValues: [...OPEN_CORRECTIVE_ACTION_STATUSES] },
    ];
    if (filters?.client) values.push({ property: 'client', propType: 'STRING', propValue: filters.client });
    if (filters?.plant) values.push({ property: 'locationUuid', propType: 'STRING', propValue: filters.plant });
    if (filters?.riskLevel) values.push({ property: 'riskLevel', propType: 'STRING', propValue: filters.riskLevel });

    return this.http
      .post<RecentCorrectiveActionsResponseDto>(
        `${this.baseUrl}requirements/get`,
        { filter: { operation: 'AND', values } },
        { params },
      )
      .pipe(
        map((res) => {
          const items = (res.data?.requirements ?? []).map(toCorrectiveActionSummary);
          const paging = res.metadata?.paging;
          return {
            items,
            paging: {
              init: (page - 1) * CORRECTIVE_ACTIONS_PAGE_SIZE,
              limit: paging?.limit ?? CORRECTIVE_ACTIONS_PAGE_SIZE,
              total: paging?.total ?? items.length,
              totalPages: paging?.totalPages ?? 1,
            },
          };
        }),
      );
  }
}
